/* eslint-disable no-restricted-globals, no-alert */
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { EXAM_CATALOG } from '../data/examCatalog';
import {
  BankRow, BankInput, fetchBankPage, fetchBankSummary, saveBankRow, deleteBankRows, moveBankRows, renameBankCategory,
  fetchBankAll, bankToCsv, bankToJson, downloadText,
} from '../utils/questionBankAdminApi';

const PAGE_SIZE = 25;
const card: React.CSSProperties = { background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' };
const label: React.CSSProperties = { fontSize: '0.82rem', fontWeight: 600, color: 'var(--gray-700)', display: 'block', marginBottom: '5px' };
const input: React.CSSProperties = { width: '100%', padding: '9px 11px', border: '1px solid var(--gray-300)', borderRadius: '6px', fontSize: '0.92rem' };

type Draft = { id: number | null; set_no: string; source_no: string; category: string; question: string; opts: string[]; ans: number[]; explanation: string };

const emptyDraft = (set: number, srcNo: number, category: string): Draft => ({ id: null, set_no: String(set), source_no: String(srcNo), category, question: '', opts: ['', '', '', ''], ans: [], explanation: '' });
const toDraft = (r: BankRow): Draft => {
  const all = [r.option1, r.option2, r.option3, r.option4, r.option5, r.option6];
  const n = Math.max(r.total_option || 0, 2);
  return {
    id: r.id, set_no: String(r.set_no), source_no: r.source_no ? String(r.source_no) : '', category: r.category || '', question: r.question,
    opts: all.slice(0, n).map(o => o || ''),
    ans: String(r.correct_ans || '').split(',').map(x => parseInt(x, 10) - 1).filter(x => !isNaN(x) && x >= 0),
    explanation: r.explanation || '',
  };
};

const AdminQuestionBankPage: React.FC = () => {
  const navigate = useNavigate();
  const [examId, setExamId] = useState(2);
  const [tab, setTab] = useState<'questions' | 'categories'>('questions');
  const [sets, setSets] = useState<{ set: number; count: number }[]>([]);
  const [cats, setCats] = useState<{ name: string; count: number }[]>([]);
  const [maxNo, setMaxNo] = useState(0);
  const [setF, setSetF] = useState('');
  const [catF, setCatF] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<BankRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [picked, setPicked] = useState<number[]>([]);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [formErr, setFormErr] = useState('');
  const [saving, setSaving] = useState(false);
  const [bulkSet, setBulkSet] = useState('');
  const [bulkCat, setBulkCat] = useState('');
  const [exporting, setExporting] = useState(false);
  const [renaming, setRenaming] = useState<{ from: string; to: string } | null>(null);

  useEffect(() => { if (sessionStorage.getItem('adminAuth') !== '1') navigate('/admin-login'); }, [navigate]);

  const loadSummary = useCallback(async () => {
    const res = await fetchBankSummary(examId);
    if (res.success) { setSets(res.sets || []); setCats(res.categories || []); setMaxNo(res.maxSourceNo || 0); } else setError(res.error || '');
  }, [examId]);

  const loadRows = useCallback(async () => {
    setLoading(true);
    const res = await fetchBankPage({ examId, set: setF ? parseInt(setF, 10) : null, category: catF, search, page, pageSize: PAGE_SIZE });
    setLoading(false);
    if (res.success) { setRows(res.rows || []); setTotal(res.total || 0); setError(''); } else setError(res.error || 'Could not load questions.');
  }, [examId, setF, catF, search, page]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { const t = setTimeout(loadRows, search ? 300 : 0); return () => clearTimeout(t); }, [loadRows, search]);

  const refresh = async () => { setPicked([]); await Promise.all([loadSummary(), loadRows()]); };
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const submit = async () => {
    if (!draft) return;
    const opts = draft.opts.map(o => o.trim());
    const filled = opts.filter(o => o);
    const set = parseInt(draft.set_no, 10);
    if (!draft.question.trim()) return setFormErr('Question text is required.');
    if (isNaN(set) || set < 1) return setFormErr('Set number must be 1 or more.');
    if (filled.length < 2) return setFormErr('Add at least two options.');
    if (draft.ans.some(i => !opts[i])) return setFormErr('A correct answer points at an empty option.');
    const keep = opts.map((o, i) => ({ o, i })).filter(x => x.o);
    const remap = (a: number) => keep.findIndex(x => x.i === a) + 1;
    const input: BankInput = {
      exam_id: examId, set_no: set, source_no: draft.source_no ? parseInt(draft.source_no, 10) : null,
      category: draft.category.trim() || null, question: draft.question.trim(),
      option1: keep[0]?.o ?? null, option2: keep[1]?.o ?? null, option3: keep[2]?.o ?? null,
      option4: keep[3]?.o ?? null, option5: keep[4]?.o ?? null, option6: keep[5]?.o ?? null,
      total_option: keep.length,
      correct_ans: draft.ans.length ? draft.ans.map(remap).sort((a, b) => a - b).join(',') : null,
      explanation: draft.explanation.trim() || null,
    };
    setSaving(true); setFormErr('');
    const res = await saveBankRow(draft.id, input);
    setSaving(false);
    if (!res.success) return setFormErr(res.error || 'Could not save.');
    setDraft(null);
    refresh();
  };

  const removePicked = async () => {
    if (!picked.length || !confirm(`Delete ${picked.length} question(s)? This cannot be undone.`)) return;
    const res = await deleteBankRows(picked);
    if (!res.success) return alert(res.error);
    refresh();
  };
  const bulkMove = async (patch: { set_no?: number; category?: string }) => {
    if (!picked.length) return;
    const res = await moveBankRows(picked, patch);
    if (!res.success) return alert(res.error);
    setBulkSet(''); setBulkCat('');
    refresh();
  };
  const exportData = async (format: 'csv' | 'json', everything: boolean) => {
    setExporting(true);
    const res = await fetchBankAll({ examId, set: everything || !setF ? null : parseInt(setF, 10), category: everything ? '' : catF, search: everything ? '' : search });
    setExporting(false);
    if (!res.success || !res.rows) return alert(res.error || 'Export failed.');
    if (res.rows.length === 0) return alert('Nothing to export for this filter.');
    const exam = EXAM_CATALOG.find(x => x.id === examId)?.key || `exam${examId}`;
    const part = everything ? 'all' : [setF && `set${setF}`, catF && 'category', search && 'search'].filter(Boolean).join('-') || 'all';
    const name = `question_bank_${exam}_${part}_${new Date().toISOString().slice(0, 10)}`;
    if (format === 'csv') downloadText(`${name}.csv`, bankToCsv(res.rows), 'text/csv');
    else downloadText(`${name}.json`, bankToJson(res.rows), 'application/json');
  };
  const doRename = async () => {
    if (!renaming) return;
    const to = renaming.to.trim();
    if (!to || to === renaming.from) return setRenaming(null);
    if (cats.some(c => c.name === to) && !confirm(`"${to}" already exists. Merge "${renaming.from}" into it?`)) return;
    const res = await renameBankCategory(examId, renaming.from, to);
    if (!res.success) return alert(res.error);
    setRenaming(null);
    if (catF === renaming.from) setCatF(to);
    refresh();
  };

  const field = (k: keyof Draft, v: string) => setDraft(d => (d ? { ...d, [k]: v } : d));
  const toggleAns = (i: number) => setDraft(d => (d ? { ...d, ans: d.ans.includes(i) ? d.ans.filter(x => x !== i) : [...d.ans, i] } : d));

  return (
    <div style={{ minHeight: '100vh', background: 'var(--gray-50)', padding: '20px' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <Link to="/admin" style={{ fontSize: '0.9rem', color: 'var(--gray-600)', textDecoration: 'none' }}>← Back to Admin</Link>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px', margin: '8px 0 18px' }}>
          <div>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 700 }}>📘 Question Bank</h1>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)' }}>Manage the questions and categories shown on the learner question bank pages.</p>
          </div>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <select style={{ ...input, width: 'auto' }} value={examId} onChange={e => { setExamId(parseInt(e.target.value, 10)); setSetF(''); setCatF(''); setPage(0); setPicked([]); }}>
              {EXAM_CATALOG.map(x => <option key={x.id} value={x.id}>{x.short}</option>)}
            </select>
            <button className="btn btn-outline-blue" disabled={exporting} onClick={() => exportData('csv', true)} title="Download every question of this exam as CSV">{exporting ? 'Exporting…' : '⬇ Export all (CSV)'}</button>
            <button className="btn btn-blue" onClick={() => { setDraft(emptyDraft(setF ? parseInt(setF, 10) : (sets[sets.length - 1]?.set || 1), maxNo + 1, catF)); setFormErr(''); setTab('questions'); }}>➕ Add question</button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '14px' }}>
          {(['questions', 'categories'] as const).map(t => (
            <button key={t} className={`btn btn-sm ${tab === t ? 'btn-blue' : 'btn-outline-gray'}`} onClick={() => setTab(t)}>{t === 'questions' ? `Questions (${total})` : `Categories (${cats.length})`}</button>
          ))}
        </div>

        {error && <div style={{ ...card, color: 'var(--red)', marginBottom: '14px' }}>⚠️ {error}</div>}

        {draft && (
          <div style={{ ...card, border: '1.5px solid var(--blue)', marginBottom: '16px' }}>
            <h2 style={{ fontSize: '1.05rem', fontWeight: 700, marginBottom: '14px' }}>{draft.id ? `✏️ Edit question #${draft.source_no || draft.id}` : '➕ New question'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', marginBottom: '12px' }}>
              <div><label style={label}>Set no.</label><input style={input} type="number" min={1} value={draft.set_no} onChange={e => field('set_no', e.target.value)} /></div>
              <div><label style={label}>Question no.</label><input style={input} type="number" value={draft.source_no} onChange={e => field('source_no', e.target.value)} /></div>
              <div style={{ gridColumn: 'span 2' }}><label style={label}>Category</label>
                <input style={input} list="qb-cats" value={draft.category} onChange={e => field('category', e.target.value)} placeholder="Pick or type a new category" />
                <datalist id="qb-cats">{cats.map(c => <option key={c.name} value={c.name} />)}</datalist>
              </div>
            </div>
            <label style={label}>Question</label>
            <textarea style={{ ...input, minHeight: '90px' }} value={draft.question} onChange={e => field('question', e.target.value)} />
            <label style={{ ...label, marginTop: '12px' }}>Options (tick the correct answer(s); leave unused options blank)</label>
            {draft.opts.map((o, i) => (
              <div key={i} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                <input type="checkbox" checked={draft.ans.includes(i)} onChange={() => toggleAns(i)} title="Correct answer" />
                <b style={{ width: '18px' }}>{String.fromCharCode(65 + i)}</b>
                <input style={input} value={o} onChange={e => setDraft(d => (d ? { ...d, opts: d.opts.map((x, j) => (j === i ? e.target.value : x)) } : d))} />
              </div>
            ))}
            {draft.opts.length < 6 && <button className="btn btn-outline-gray btn-sm" onClick={() => setDraft(d => (d ? { ...d, opts: [...d.opts, ''] } : d))}>+ Add option</button>}
            <label style={{ ...label, marginTop: '12px' }}>Explanation</label>
            <textarea style={{ ...input, minHeight: '90px' }} value={draft.explanation} onChange={e => field('explanation', e.target.value)} />
            {formErr && <div style={{ marginTop: '10px', padding: '9px 12px', background: 'var(--red-pale)', color: 'var(--red)', borderRadius: '6px', fontSize: '0.88rem' }}>{formErr}</div>}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '14px' }}>
              <button className="btn btn-outline-gray" onClick={() => setDraft(null)}>Cancel</button>
              <button className="btn btn-blue" onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        )}

        {tab === 'questions' && (
          <>
            <div style={{ ...card, marginBottom: '14px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <input style={{ ...input, flex: '2 1 220px', width: 'auto' }} placeholder="Search question text…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} />
              <select style={{ ...input, flex: '1 1 130px', width: 'auto' }} value={setF} onChange={e => { setSetF(e.target.value); setPage(0); }}>
                <option value="">All sets</option>{sets.map(s => <option key={s.set} value={s.set}>Set {s.set} ({s.count})</option>)}
              </select>
              <button className="btn btn-outline-gray btn-sm" disabled={exporting} onClick={() => exportData('csv', false)} title="Exports the questions matching the filters above">⬇ CSV (filtered)</button>
              <button className="btn btn-outline-gray btn-sm" disabled={exporting} onClick={() => exportData('json', false)} title="Exports the questions matching the filters above">⬇ JSON (filtered)</button>
              <select style={{ ...input, flex: '2 1 200px', width: 'auto' }} value={catF} onChange={e => { setCatF(e.target.value); setPage(0); }}>
                <option value="">All categories</option>{cats.map(c => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
              </select>
            </div>

            {picked.length > 0 && (
              <div style={{ ...card, marginBottom: '14px', background: 'var(--blue-pale)', display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                <strong>{picked.length} selected</strong>
                <input style={{ ...input, width: '110px' }} type="number" min={1} placeholder="Set no." value={bulkSet} onChange={e => setBulkSet(e.target.value)} />
                <button className="btn btn-outline-blue btn-sm" disabled={!bulkSet} onClick={() => bulkMove({ set_no: parseInt(bulkSet, 10) })}>Move to set</button>
                <input style={{ ...input, width: '200px' }} list="qb-cats" placeholder="Category" value={bulkCat} onChange={e => setBulkCat(e.target.value)} />
                <button className="btn btn-outline-blue btn-sm" disabled={!bulkCat.trim()} onClick={() => bulkMove({ category: bulkCat.trim() })}>Set category</button>
                <button className="btn btn-sm" style={{ background: '#fff', color: 'var(--red)', border: '1px solid var(--red)' }} onClick={removePicked}>🗑 Delete</button>
                <button className="btn btn-outline-gray btn-sm" onClick={() => setPicked([])}>Clear</button>
              </div>
            )}

            {loading ? <div style={{ textAlign: 'center', padding: '30px', color: 'var(--gray-500)' }}>Loading…</div> : rows.length === 0 ? (
              <div style={{ ...card, textAlign: 'center', color: 'var(--gray-500)' }}>No questions match.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '0.82rem', color: 'var(--gray-600)' }}>
                  <input type="checkbox" checked={rows.every(r => picked.includes(r.id))} onChange={e => setPicked(e.target.checked ? Array.from(new Set([...picked, ...rows.map(r => r.id)])) : picked.filter(id => !rows.some(r => r.id === id)))} /> Select all on this page
                </label>
                {rows.map(r => (
                  <div key={r.id} style={{ ...card, padding: '12px 14px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                    <input type="checkbox" checked={picked.includes(r.id)} onChange={() => setPicked(picked.includes(r.id) ? picked.filter(x => x !== r.id) : [...picked, r.id])} style={{ marginTop: '4px' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)', marginBottom: '3px' }}>
                        #{r.source_no ?? r.id} · Set {r.set_no} · {r.category || 'no category'} · {r.correct_ans ? `answer ${r.correct_ans}` : <span style={{ color: 'var(--orange)', fontWeight: 700 }}>no answer key</span>}
                      </div>
                      <div style={{ fontSize: '0.9rem', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>{r.question}</div>
                    </div>
                    <button className="btn btn-outline-blue btn-sm" onClick={() => { setDraft(toDraft(r)); setFormErr(''); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>✏️ Edit</button>
                  </div>
                ))}
              </div>
            )}

            {pages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '14px', marginTop: '16px' }}>
                <button className="btn btn-outline-blue btn-sm" disabled={page === 0} onClick={() => setPage(page - 1)}>← Prev</button>
                <span style={{ fontWeight: 600 }}>Page {page + 1} / {pages}</span>
                <button className="btn btn-outline-blue btn-sm" disabled={page >= pages - 1} onClick={() => setPage(page + 1)}>Next →</button>
              </div>
            )}
          </>
        )}

        {tab === 'categories' && (
          <div style={card}>
            <p style={{ fontSize: '0.85rem', color: 'var(--gray-500)', marginBottom: '12px' }}>Rename a category to fix a typo, or rename it to an existing name to merge the two. This updates every question in the exam.</p>
            {cats.map(c => (
              <div key={c.name} style={{ display: 'flex', gap: '10px', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap' }}>
                {renaming && renaming.from === c.name ? (
                  <>
                    <input style={{ ...input, flex: 1, minWidth: '200px' }} list="qb-cats" value={renaming.to} onChange={e => setRenaming({ from: c.name, to: e.target.value })} autoFocus />
                    <button className="btn btn-blue btn-sm" onClick={doRename}>Save</button>
                    <button className="btn btn-outline-gray btn-sm" onClick={() => setRenaming(null)}>Cancel</button>
                  </>
                ) : (
                  <>
                    <span style={{ flex: 1, fontWeight: 600 }}>{c.name}</span>
                    <span style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>{c.count} questions</span>
                    <button className="btn btn-outline-gray btn-sm" onClick={() => { setCatF(c.name); setPage(0); setTab('questions'); }}>View</button>
                    {c.name !== '(none)' && <button className="btn btn-outline-blue btn-sm" onClick={() => setRenaming({ from: c.name, to: c.name })}>Rename / merge</button>}
                  </>
                )}
              </div>
            ))}
            <datalist id="qb-cats">{cats.map(c => <option key={c.name} value={c.name} />)}</datalist>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminQuestionBankPage;
